/**
 * 遊戲 HTML 安全檢查器
 *
 * 用途: 檢查自定義遊戲 HTML 中的潛在安全問題
 *
 * 策略:
 * - 只檢查不修改 (檢測危險模式但不刪除程式碼)
 * - 依賴 iframe sandbox="allow-scripts" 提供隔離
 * - 主要防護在後端 (CodeSanitizationService + AIValidationService)
 *
 * 檢查項目:
 * - inline event handlers (onclick, onerror 等)
 * - 巢狀 iframe, object, embed 標籤
 * - javascript: URI
 *
 * @author Claude Code
 * @version 2.0
 */

// 注意:此版本不使用 DOMPurify 進行 HTML 清理
// 原因:DOMPurify 會清空 <script> 標籤內容,導致遊戲無法執行
// 策略:只檢查危險模式,不修改 HTML,依賴 iframe sandbox 隔離

class GameSanitizer {
  constructor(options = {}) {
    this.isDevelopment = options.isDevelopment ?? (process.env.NODE_ENV === 'development');
    this.logIssues = options.logIssues ?? this.isDevelopment;
    this.throwOnError = options.throwOnError ?? false;

    this.stats = {
      totalChecked: 0,
      issuesFound: 0,
      totalDuration: 0
    };
  }

  /**
   * 檢查遊戲 HTML (只檢查不修改)
   * @param {string} html - 原始 HTML
   * @returns {Object} { clean: string, issues: array, stats: object, safe: boolean }
   */
  sanitize(html) {
    const startTime = performance.now();
    const issues = [];

    try {
      // 1. 快速檢查 (正則表達式)
      const quickCheckResult = this.quickCheck(html);
      if (!quickCheckResult.passed) {
        issues.push(...quickCheckResult.issues);
      }

      // 2. 檢查危險的 inline event handlers
      const inlineEventCheck = this.checkInlineEvents(html);
      if (!inlineEventCheck.passed) {
        issues.push(...inlineEventCheck.issues);
      }

      // 3. 統計
      const duration = performance.now() - startTime;
      this.recordStats(issues.length, duration);

      // 4. 記錄問題 (開發模式)
      if (this.logIssues && issues.length > 0) {
        this.logSanitizeIssues(issues);
      }

      // 5. 判斷是否安全
      const hasErrors = issues.some(i => i.severity === 'error');
      const safe = !hasErrors;

      // ⚠️ 重要:返回原始 HTML (不修改)
      // 真正的安全防護由 iframe sandbox 和後端驗證提供
      return {
        clean: html,  // 不修改原始 HTML
        issues: issues,
        safe: safe,
        stats: {
          originalSize: html.length,
          cleanSize: html.length,
          duration: Math.round(duration),
          issueCount: issues.length
        }
      };

    } catch (error) {
      console.error('[GameSanitizer] 檢查錯誤:', error);

      return {
        clean: html,
        issues: [{
          type: 'check-error',
          severity: 'error',
          message: `檢查處理失敗: ${error.message}`
        }],
        safe: false,
        stats: {
          originalSize: html.length,
          cleanSize: html.length,
          duration: 0,
          issueCount: 1
        }
      };
    }
  }

  /**
   * 檢查危險的 inline event handlers
   * @param {string} html
   * @returns {Object} { passed: boolean, issues: array }
   */
  checkInlineEvents(html) {
    const issues = [];

    const dangerousEventAttrs = [
      'onclick', 'ondblclick', 'onmousedown', 'onmouseup', 'onmouseover', 'onmouseout', 'onmousemove',
      'onkeydown', 'onkeyup', 'onkeypress',
      'onfocus', 'onblur', 'onchange', 'onsubmit', 'onreset',
      'onload', 'onerror', 'onabort', 'onunload',
      'onresize', 'onscroll'
    ];

    dangerousEventAttrs.forEach(attr => {
      const pattern = new RegExp(`\\s${attr}\\s*=`, 'i');
      if (pattern.test(html)) {
        issues.push({
          type: 'inline-event-handler',
          severity: 'warning',
          message: `偵測到 ${attr} 屬性,建議改用 addEventListener`
        });
      }
    });

    return {
      passed: issues.length === 0,
      issues: issues
    };
  }

  /**
   * 快速檢查 - 使用正則表達式檢測明顯的危險模式
   * @param {string} html
   * @returns {Object} { passed: boolean, issues: array }
   */
  quickCheck(html) {
    const issues = [];

    const dangerousPatterns = [
      {
        pattern: /<iframe[^>]*>/i,
        message: '檢測到 iframe 標籤 (將被移除)',
        severity: 'warning'
      },
      {
        pattern: /<object[^>]*>/i,
        message: '檢測到 object 標籤 (將被移除)',
        severity: 'warning'
      },
      {
        pattern: /<embed[^>]*>/i,
        message: '檢測到 embed 標籤 (將被移除)',
        severity: 'warning'
      },
      {
        pattern: /javascript:\s*[^'"]/i,
        message: '檢測到 javascript: URI (將被移除)',
        severity: 'warning'
      },
      {
        pattern: /<applet[^>]*>/i,
        message: '檢測到 applet 標籤 (將被移除)',
        severity: 'warning'
      }
    ];

    dangerousPatterns.forEach(({ pattern, message, severity }) => {
      if (pattern.test(html)) {
        issues.push({
          type: 'quick-check',
          severity: severity,
          message: message
        });
      }
    });

    return {
      passed: issues.length === 0,
      issues: issues
    };
  }


  /**
   * 記錄統計資訊
   * @param {number} issuesCount
   * @param {number} duration
   */
  recordStats(issuesCount, duration) {
    this.stats.totalChecked++;
    this.stats.issuesFound += issuesCount;
    this.stats.totalDuration += duration;

    // 開發模式: 輸出統計
    if (this.isDevelopment) {
      console.log(
        `[GameSanitizer] #${this.stats.totalChecked} | ` +
        `${duration.toFixed(2)}ms | ` +
        `${issuesCount} issues`
      );
    }
  }

  /**
   * 記錄 Sanitize 問題 (開發模式)
   * @param {Array} issues
   */
  logSanitizeIssues(issues) {
    console.group('[GameSanitizer] 發現問題');

    // 按嚴重程度分組
    const errorIssues = issues.filter(i => i.severity === 'error');
    const warningIssues = issues.filter(i => i.severity === 'warning');
    const infoIssues = issues.filter(i => i.severity === 'info');

    if (errorIssues.length > 0) {
      console.group(`❌ 錯誤 (${errorIssues.length})`);
      errorIssues.forEach(issue => {
        console.error(`[${issue.type}] ${issue.message}`);
      });
      console.groupEnd();
    }

    if (warningIssues.length > 0) {
      console.group(`⚠️ 警告 (${warningIssues.length})`);
      warningIssues.forEach(issue => {
        console.warn(`[${issue.type}] ${issue.message}`);
      });
      console.groupEnd();
    }

    if (infoIssues.length > 0) {
      console.group(`ℹ️ 資訊 (${infoIssues.length})`);
      infoIssues.forEach(issue => {
        console.info(`[${issue.type}] ${issue.message}`);
      });
      console.groupEnd();
    }

    console.groupEnd();
  }

  /**
   * 取得統計資訊
   * @returns {Object} 統計資料
   */
  getStats() {
    return {
      ...this.stats,
      averageDuration: this.stats.totalChecked > 0
        ? (this.stats.totalDuration / this.stats.totalChecked).toFixed(2)
        : 0
    };
  }

  /**
   * 重置統計
   */
  resetStats() {
    this.stats = {
      totalChecked: 0,
      issuesFound: 0,
      totalDuration: 0
    };
  }
}

// 單例模式
const gameSanitizer = new GameSanitizer();

export default gameSanitizer;

/**
 * 便捷方法 - 直接 sanitize HTML
 * @param {string} html - 原始 HTML
 * @returns {Object} { clean, issues, stats }
 */
export function sanitizeGameHTML(html) {
  return gameSanitizer.sanitize(html);
}

/**
 * 取得 sanitizer 實例 (用於配置或統計)
 * @returns {GameSanitizer}
 */
export function getSanitizer() {
  return gameSanitizer;
}
